import React from 'react';
import classNames from 'classnames';
import { ICONS } from 'src/common/constants';
import IcoMoon from '../IcoMoon';
import { convertLocalTime } from 'src/helpers/validationHelper';
import FontAwesomeIcon from '../Icon';
import TimelineSkeleton from 'src/components/Timeline/skeletons/TimelineSkeleton';
import { displayStatusLabel } from 'src/utils/serviceRequestStatus/displayStatusLabel';
import './Timeline.scss';

interface Actor {
  firstname: string;
  lastname: string;
}

interface ActivityEvent {
  statusName: string;
  customLabel?: string | null;
  timestamp?: string | number;
  actor: Actor;
  action: string;
  reasonType?: string;
  reason?: string;
}

interface TimelineProps {
  activity: ActivityEvent[];
  loading: boolean;
}

const decodeUnicodeEscapeSequences = (input: string): string => {
  const replacedUnicode: string = input.replace(
    /(\\)?\\u(\w{4})/g,
    (match, escaped, p1) => {
      if (escaped) {
        return match;
      }

      return String.fromCharCode(parseInt(p1, 16));
    }
  );

  return replacedUnicode.replace(/\\n/g, '\n');
};

interface TimelineIconProps {
  statusName: string;
}

const TimelineIcon = ({ statusName }: TimelineIconProps) => {
  return (
    <div className="timeline-icon timeline-icon-hourglass">
      {statusName === 'Under Review' ? (
        <FontAwesomeIcon glyph="hourglass-end" weight="light" />
      ) : statusName === 'One Year Warranty' ? (
        <FontAwesomeIcon glyph="certificate" weight="solid" size="solid" />
      ) : (
        <IcoMoon glyph={ICONS[statusName]} />
      )}
    </div>
  );
};

interface DeclinedReasonProps {
  event: ActivityEvent;
}

const DeclinedReason = ({ event }: DeclinedReasonProps) => {
  if (event.statusName !== 'Declined') {
    return null;
  }

  return (
    <>
      <div className="timeline-log">Reason Type: {event.reasonType}</div>
      <div className="timeline-log">
        Reason Description:{' '}
        {event.reason ? decodeUnicodeEscapeSequences(event.reason) : ''}
      </div>
    </>
  );
};

interface TimelineItemProps {
  event: ActivityEvent;
  index: number;
}

const TimelineItem = ({ event, index }: TimelineItemProps) => (
  <li
    className={classNames(
      'timeline-item',
      `timeline-item-${index === 0 ? 'present' : 'past'}`
    )}
  >
    <div className="timeline-bar" />
    <div className="timeline-details">
      <span className="timeline-type">
        {displayStatusLabel(event.statusName, event.customLabel)}
      </span>{' '}
      {event.timestamp ? convertLocalTime(event.timestamp) : null}
    </div>
    <div className="timeline-log">
      {event.actor.firstname} {event.actor.lastname}
    </div>
    <div className="timeline-log">{event.action}</div>
    <DeclinedReason event={event} />
    <TimelineIcon statusName={event.statusName} />
  </li>
);

interface TimelineBodyProps {
  activity: ActivityEvent[];
  loading: boolean;
}

const TimelineBody = ({ activity, loading }: TimelineBodyProps) => {
  if (loading) {
    return <TimelineSkeleton />;
  }

  if (!activity || activity.length === 0) {
    return (
      <div className="timeline-title" style={{ fontSize: 10, color: 'grey' }}>
        No activity exists for this Service Request yet.
      </div>
    );
  }

  return (
    <ul className="timeline-items">
      {activity.map((event: ActivityEvent, index: number) => (
        <TimelineItem key={index} event={event} index={index} />
      ))}
    </ul>
  );
};

const Timeline = ({ activity, loading }: TimelineProps) => (
  <div className="timeline">
    <div className="timeline-title">Activity</div>
    <TimelineBody activity={activity} loading={loading} />
  </div>
);

export default Timeline;
